/* eslint-disable react/jsx-no-constructed-context-values */
/**
 * Current user context: fetches /api/users/me and provides user + role for RBAC.
 */
import { createContext, useContext, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { protectedResources, siteData } from "../authConfig";

const UserContext = createContext(null);
UserContext.displayName = "UserContext";

export function UserControllerProvider({ children }) {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // access: "loading" → waiting on /users/me; "ok" → a member of this workspace;
  // "denied" → authenticated but NOT a member of this tenant (show the no-access screen, never the app);
  // "error" → couldn't load the profile (transient/expired session) — also never render the app.
  const [access, setAccess] = useState("loading");
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    const base = protectedResources?.apiTodoList?.endpoint || "";
    if (!base) {
      setAccess("error");
      setLoading(false);
      return;
    }
    execute("GET", `${base}/users/me`)
      .then((data) => {
        if (data && data.id) {
          setUser(data);
          setAccess("ok");
        } else if (data && (data.reason === "not_a_member" || data.access === "denied")) {
          // Valid token, but this identity isn't a member of this workspace.
          setUser(null);
          setAccess("denied");
        } else {
          // Unexpected shape or a transient/expired-session response — don't render the app shell.
          setUser(null);
          setAccess("error");
        }
        setLoading(false);
      })
      .catch(() => {
        setUser(null);
        setAccess("error");
        setLoading(false);
      });
  }, [execute]);

  const currentRole = user?.role || "VIEW";
  const value = {
    user,
    role: currentRole,
    loading,
    access,
    // SUPER_RISK_ADMIN is a superset of RISK_ADMIN — keep all admin UI for super users.
    isRiskAdmin: currentRole === "RISK_ADMIN" || currentRole === "SUPER_RISK_ADMIN",
    isSuperRiskAdmin: currentRole === "SUPER_RISK_ADMIN",
    categoryAttachments: Array.isArray(user?.categoryAttachments) ? user.categoryAttachments : [],
    modules: user?.modules || {},
    refetch: () => {
      fetched.current = false;
      setLoading(true);
      setAccess("loading");
    },
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

UserControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useCurrentUser() {
  const ctx = useContext(UserContext);
  return (
    ctx || {
      user: null,
      role: "VIEW",
      loading: false,
      access: "loading",
      isRiskAdmin: false,
      isSuperRiskAdmin: false,
      modules: {},
    }
  );
}
